import { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
export const remKey = (n) => `${n.id}:${n.reminder_at}`;

export function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function toLocalInputValue(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Advance a recurring reminder to its next future occurrence (calendar-aware
// for monthly / annually).
export function advanceReminderAt(iso, rule) {
  const start = new Date(iso);
  if (isNaN(start.getTime())) return null;
  const now = Date.now();
  const bump = (dt) => {
    const x = new Date(dt);
    if (rule === 'twicedaily') x.setHours(x.getHours() + 12);
    else if (rule === 'weekly') x.setDate(x.getDate() + 7);
    else if (rule === 'monthly') x.setMonth(x.getMonth() + 1);
    else if (rule === 'annually') x.setFullYear(x.getFullYear() + 1);
    else x.setDate(x.getDate() + 1); // daily / fallback
    return x;
  };
  let dt = new Date(start);
  let guard = 0;
  while (dt.getTime() <= now && guard < 4000) { dt = bump(dt); guard += 1; }
  return dt.toISOString();
}

// ---------------------------------------------------------------------------
// In-browser alarm WAV (no asset) — an attention chime, unlocked on first
// gesture so it can fire later on iOS/Safari.
// ---------------------------------------------------------------------------
let _alarmSrc = null;
export function alarmDataUri() {
  if (_alarmSrc) return _alarmSrc;
  const sr = 8000;
  const pattern = [
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 }, { f: 1319, d: 0.16 }, { f: 0, d: 0.05 },
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 }, { f: 1319, d: 0.16 }, { f: 0, d: 0.12 },
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 }, { f: 1319, d: 0.28 },
  ];
  let total = 0;
  pattern.forEach((b) => { total += Math.floor(b.d * sr); });
  const samples = new Int16Array(total);
  let idx = 0;
  pattern.forEach((b) => {
    const n = Math.floor(b.d * sr);
    for (let i = 0; i < n; i++) {
      let s = 0;
      if (b.f > 0) {
        const env = Math.min(1, i / 48) * Math.min(1, (n - i) / 48);
        s = Math.sin(2 * Math.PI * b.f * (i / sr)) * env * 0.92;
      }
      samples[idx++] = Math.max(-1, Math.min(1, s)) * 32767;
    }
  });
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const w = (o, str) => { for (let i = 0; i < str.length; i++) view.setUint8(o + i, str.charCodeAt(i)); };
  w(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); w(8, 'WAVE');
  w(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  w(36, 'data'); view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) { view.setInt16(off, samples[i], true); off += 2; }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  _alarmSrc = 'data:audio/wav;base64,' + btoa(binary);
  return _alarmSrc;
}

// ---------------------------------------------------------------------------
// useTaskReminders — mirrors the Notes reminder system for the Tasks module.
// Polls /notes/tasks/reminders, surfaces due tasks in a stack, plays a chime +
// OS notification, supports snooze presets + custom, and advances recurring
// reminders (daily/weekly/monthly/annually) for tasks I own.
// ---------------------------------------------------------------------------
export function useTaskReminders({ onOpen } = {}) {
  const [dueQueue, setDueQueue] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [notifPerm, setNotifPerm] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));

  const soundedRef = useRef(new Set());
  const ackRef = useRef(null);
  const snoozeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const alarmRef = useRef(null);
  const alarmUnlockedRef = useRef(false);
  const primedRef = useRef(false); // first poll only primes (no chime) so opening never sounds
  const onOpenRef = useRef(onOpen);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);

  const getAck = () => {
    if (!ackRef.current) {
      try { ackRef.current = new Set(JSON.parse(localStorage.getItem('tasks:remAck') || '[]')); }
      catch { ackRef.current = new Set(); }
    }
    return ackRef.current;
  };
  const persistAck = () => { try { localStorage.setItem('tasks:remAck', JSON.stringify([...getAck()])); } catch { /* noop */ } };

  const getSnooze = () => {
    if (!snoozeRef.current) {
      try { snoozeRef.current = JSON.parse(localStorage.getItem('tasks:remSnooze') || '{}'); }
      catch { snoozeRef.current = {}; }
    }
    return snoozeRef.current;
  };
  const persistSnooze = () => { try { localStorage.setItem('tasks:remSnooze', JSON.stringify(getSnooze())); } catch { /* noop */ } };
  const setLocalSnooze = (n, untilMs) => { const s = getSnooze(); s[remKey(n)] = untilMs; persistSnooze(); soundedRef.current.delete(remKey(n)); };

  const playChime = useCallback(() => {
    try {
      const a = alarmRef.current;
      if (a) { a.muted = false; a.volume = 1; a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); }
    } catch { /* noop */ }
    try { if (navigator.vibrate) navigator.vibrate([250, 120, 250, 120, 400]); } catch { /* noop */ }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      let ctx = audioCtxRef.current;
      if (!ctx) { ctx = new Ctor(); audioCtxRef.current = ctx; }
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const base = ctx.currentTime + 0.02;
      [880, 1174.66, 1567.98, 1174.66, 1567.98].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle'; o.frequency.value = freq;
        const t0 = base + i * 0.2;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
        o.connect(g); g.connect(ctx.destination);
        o.start(t0); o.stop(t0 + 0.65);
      });
    } catch { /* audio unsupported */ }
  }, []);

  const acknowledge = useCallback((n) => {
    const k = remKey(n);
    getAck().add(k); persistAck();
    setDueQueue((q) => q.filter((x) => remKey(x) !== k));
  }, []);

  const openFromReminder = useCallback((n) => { acknowledge(n); onOpenRef.current?.(n.id); }, [acknowledge]);

  const applySnooze = useCallback(async (n, targetMs, label, notify) => {
    setDueQueue((q) => q.filter((x) => x.id !== n.id));
    if (n.owned === false) { setLocalSnooze(n, targetMs); notify?.(label); return; }
    soundedRef.current.add(remKey(n));
    try { await notesApi.patch(`/notes/tasks/tasks/${n.id}`, { reminder_at: new Date(targetMs).toISOString() }); notify?.(label); }
    catch { notify?.('Could not snooze reminder.'); }
  }, []);

  const snooze = useCallback((n, minutes, notify) => (
    applySnooze(n, Date.now() + minutes * 60000, minutes >= 60 ? 'Snoozed for 1 hour.' : `Snoozed for ${minutes} minutes.`, notify)
  ), [applySnooze]);

  const snoozeUntilTomorrow = useCallback((n, notify) => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0);
    return applySnooze(n, d.getTime(), 'Snoozed until tomorrow 10am.', notify);
  }, [applySnooze]);

  // "Later today" — jump to this evening (6 PM), or +2h if it's already evening.
  const snoozeLaterToday = useCallback((n, notify) => {
    const d = new Date();
    if (d.getHours() < 18) d.setHours(18, 0, 0, 0);
    else d.setTime(d.getTime() + 2 * 3600000);
    return applySnooze(n, d.getTime(), 'Snoozed to later today.', notify);
  }, [applySnooze]);

  // "Tomorrow morning" — tomorrow at 9 AM.
  const snoozeTomorrowMorning = useCallback((n, notify) => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    return applySnooze(n, d.getTime(), 'Snoozed to tomorrow morning.', notify);
  }, [applySnooze]);

  const snoozeCustom = useCallback((n, localDateTimeStr, notify) => {
    const t = new Date(localDateTimeStr).getTime();
    if (isNaN(t)) { notify?.('Please pick a valid date and time.'); return false; }
    if (t <= Date.now()) { notify?.('Pick a time in the future.'); return false; }
    applySnooze(n, t, `Snoozed until ${formatWhen(new Date(t).toISOString())}.`, notify);
    return true;
  }, [applySnooze]);

  const refreshUpcoming = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/tasks/reminders'); setUpcomingReminders(data || []); }
    catch { /* silent */ }
  }, []);

  // Poll for due reminders.
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { data } = await notesApi.get('/notes/tasks/reminders');
        if (stop) return;
        setUpcomingReminders(data || []);
        const now = Date.now();
        const ack = getAck();
        const snoozeStore = getSnooze();
        let snoozeChanged = false;
        Object.keys(snoozeStore).forEach((k) => { if (snoozeStore[k] <= now) { delete snoozeStore[k]; snoozeChanged = true; } });
        if (snoozeChanged) persistSnooze();
        const due = (data || []).filter((n) => {
          if (n.is_completed) return false;
          if (!n.reminder_at || new Date(n.reminder_at).getTime() > now) return false;
          if (ack.has(remKey(n))) return false;
          const until = snoozeStore[remKey(n)];
          if (until && until > now) return false;
          return true;
        });
        setDueQueue(due);
        const fresh = due.filter((n) => !soundedRef.current.has(remKey(n)));
        // The first poll after the module opens only PRIMES the alerted set —
        // it never chimes or fires an OS notification for reminders that were
        // already due when you arrived. Only reminders that come due while the
        // app is open (a later poll) alert with sound. This stops the "chime on
        // every open" annoyance while keeping live reminders working.
        const wasPrimed = primedRef.current;
        primedRef.current = true;
        if (fresh.length) {
          fresh.forEach((n) => soundedRef.current.add(remKey(n)));
          if (wasPrimed) {
            playChime();
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                fresh.forEach((n) => {
                  const notif = new Notification('Task reminder', { body: n.title || 'Untitled task', tag: `tasks-rem-${n.id}`, requireInteraction: true });
                  notif.onclick = () => { try { window.focus(); } catch { /* noop */ } openFromReminder(n); try { notif.close(); } catch { /* noop */ } };
                });
              }
            } catch { /* unsupported */ }
          }
          // Advance recurring reminders (owned tasks only) — even on the first
          // poll, so a due recurrence still rolls forward.
          fresh.forEach((n) => {
            const rule = n.reminder_rule;
            if (n.owned !== false && rule && rule !== 'none') {
              const next = advanceReminderAt(n.reminder_at, rule);
              if (next) notesApi.patch(`/notes/tasks/tasks/${n.id}`, { reminder_at: next, reminder_rule: rule }).catch(() => {});
            }
          });
        }
      } catch { /* silent */ }
    };
    check();
    const iv = setInterval(check, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stop = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playChime, openFromReminder]);

  const requestPermission = useCallback(() => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().then((p) => setNotifPerm(p)).catch(() => {});
      }
    } catch { /* unsupported */ }
  }, []);

  // Unlock audio + request permission on the first user gesture inside the app.
  useEffect(() => {
    const unlock = () => {
      requestPermission();
      try {
        if (!alarmRef.current) { const a = new Audio(alarmDataUri()); a.preload = 'auto'; alarmRef.current = a; }
        if (!alarmUnlockedRef.current) {
          const a = alarmRef.current;
          a.muted = true; a.volume = 0;
          const done = () => { try { a.pause(); a.currentTime = 0; a.muted = false; a.volume = 1; } catch { /* noop */ } };
          const p = a.play();
          if (p && p.then) p.then(done).catch(() => { a.muted = false; a.volume = 1; });
          else done();
          alarmUnlockedRef.current = true;
        }
      } catch { /* noop */ }
    };
    const opts = { once: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    return () => { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  }, [requestPermission]);

  return {
    dueQueue, upcomingReminders, notifPerm,
    acknowledge, openFromReminder, snooze, snoozeUntilTomorrow, snoozeLaterToday, snoozeTomorrowMorning, snoozeCustom,
    refreshUpcoming, requestPermission, playChime,
  };
}
